// src/pages/TrackCreation/Step1.tsx
import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TrackData } from "../../context/types";
import { useGlobalState } from "../../context/useGlobalState";
import {
    getScanConfig,
    getScannableObjects,
    getProgressionConfig,
    getLevels,
    getBadges,
    getRewards,
} from "../../api/progression";
import { getWaypointsForTrack } from "../../api/waypoint";
import LoadingSpinner from "../ui/LoadingSpinner";
import shell from "../../pages/track/create/createShell.module.css";

interface SidebarItem {
    title: string;
    link?: string;
    prerequisitesMet?: boolean;
    subcategories?: Subcategory[];
    hasSubcategories?: boolean;
}

interface Subcategory {
    name: string;
    link: string;
    prerequisitesMet: boolean;
}

interface SidebarProps {
    trackData: TrackData;
}

const Sidebar: React.FC<SidebarProps> = ({ trackData }) => {
    const location = useLocation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data: globalState } = useGlobalState();

    // Loading state for sidebar - only for initial load
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const hasLoadedOnce = useRef(false);

    // Track configuration state for conditional sidebar items
    const [hasScanConfig, setHasScanConfig] = useState(false);
    const [hasObjects, setHasObjects] = useState(false);
    const [hasStories, setHasStories] = useState(false);

    // Progression steps state
    const [hasAvatar, setHasAvatar] = useState(false);
    const [hasLevels, setHasLevels] = useState(false);
    const [, setHasBadges] = useState(false);
    const [, setHasRewards] = useState(false);

    // Fetch scan config and objects status for conditional sidebar rendering
    // All state updates are batched at the end to prevent progressive loading
    const fetchConfigStatus = async (): Promise<void> => {
        const token = localStorage.getItem("token");

        // Collect all state values first, then update all at once
        let newHasScanConfig = false;
        let newHasObjects = false;
        let newHasStories = false;
        let newHasAvatar = false;
        let newHasLevels = false;
        let newHasBadges = false;
        let newHasRewards = false;

        if (!token || !trackData?.trackId) {
            // Batch update all states at once
            setHasScanConfig(newHasScanConfig);
            setHasObjects(newHasObjects);
            setHasStories(newHasStories);
            setHasAvatar(newHasAvatar);
            setHasLevels(newHasLevels);
            setHasBadges(newHasBadges);
            setHasRewards(newHasRewards);
            setIsInitialLoading(false);
            hasLoadedOnce.current = true;
            return;
        }

        try {
            const configResponse = await getScanConfig({ token, trackId: trackData.trackId });
            if (configResponse?.scanConfig) {
                newHasScanConfig = true;

                const objectsResponse = await getScannableObjects({
                    token,
                    scanConfigId: configResponse.scanConfig.id,
                });
                const objectsExist = (objectsResponse?.scannableObjects?.length || 0) > 0;
                newHasObjects = objectsExist;

                // Check for stories (waypoints with IMAGE_SCAN, BOTH, or INVISIBLE trigger mode)
                // Include both definitive and temporary (draft) waypoints
                let storiesExist = false;
                if (objectsExist) {
                    const waypointsResponse = await getWaypointsForTrack({
                        token,
                        trackId: trackData.trackId,
                    });
                    const definitiveWaypoints =
                        waypointsResponse?.waypoints?.filter(
                            (wpt: any) =>
                                wpt.triggerMode === "IMAGE_SCAN" ||
                                wpt.triggerMode === "BOTH" ||
                                wpt.triggerMode === "INVISIBLE",
                        ) || [];
                    const temporaryWaypoints =
                        waypointsResponse?.temporaryWaypoints?.filter(
                            (wpt: any) =>
                                wpt.triggerMode === "IMAGE_SCAN" ||
                                wpt.triggerMode === "BOTH" ||
                                wpt.triggerMode === "INVISIBLE",
                        ) || [];
                    const totalStories = definitiveWaypoints.length + temporaryWaypoints.length;
                    storiesExist = totalStories > 0;
                    newHasStories = storiesExist;
                }

                // Check progression steps if progression is enabled and has stories
                const hasProgressionSystem = trackData?.hasProgressionSystem || false;
                if (hasProgressionSystem && storiesExist) {
                    try {
                        const progressionConfigResponse = await getProgressionConfig({
                            token,
                            trackId: trackData.trackId,
                        });

                        if (progressionConfigResponse?.progressionConfig) {
                            const progressionConfigId = progressionConfigResponse.progressionConfig.id;

                            // Check avatar (always available once progression config exists)
                            newHasAvatar = true;

                            // Check levels
                            const levelsResponse = await getLevels({
                                token,
                                progressionConfigId,
                            });
                            newHasLevels = (levelsResponse?.levels?.length || 0) > 0;

                            // Check badges
                            const badgesResponse = await getBadges({
                                token,
                                progressionConfigId,
                            });
                            newHasBadges = (badgesResponse?.badges?.length || 0) > 0;

                            // Check rewards
                            const rewardsResponse = await getRewards({
                                token,
                                progressionConfigId,
                            });
                            newHasRewards = (rewardsResponse?.rewards?.length || 0) > 0;
                        }
                    } catch (error) {
                        console.error("Error fetching progression status:", error);
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching config status:", error);
        } finally {
            // Batch update all states at once to prevent progressive loading
            setHasScanConfig(newHasScanConfig);
            setHasObjects(newHasObjects);
            setHasStories(newHasStories);
            setHasAvatar(newHasAvatar);
            setHasLevels(newHasLevels);
            setHasBadges(newHasBadges);
            setHasRewards(newHasRewards);
            setIsInitialLoading(false);
            hasLoadedOnce.current = true;
        }
    };

    useEffect(() => {
        fetchConfigStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackData?.trackId, location.pathname]);

    // Determine the primary link identifier
    const linkId = trackData?.trackId && trackData.trackId !== "" ? trackData.trackId : trackData.id;

    // Determine tour type
    const isMuseum = trackData?.type === "iduniquemusee";
    const isHoho = trackData?.type === "iduniquebus";

    // Check if this is an image scan track
    const currentFilter = globalState?.filtersAI?.find((f) => f.value === trackData?.subType);
    const isImageScan = currentFilter?.hasOnlyImageScanWaypoints || false;
    const hasProgressionSystem = trackData?.hasProgressionSystem || false;

    // Unlocking conditions
    const isStep2Unlocked = !!(trackData?.language && trackData?.type);
    const isStepMuseumInfoUnlocked = !!(trackData?.title && isStep2Unlocked);
    const isStep3Unlocked = isMuseum
        ? isStepMuseumInfoUnlocked &&
          trackData?.museumName != null &&
          trackData?.museumInfo != null &&
          trackData?.museumPictureUrl != null
        : !!(trackData?.title != null && isStep2Unlocked);
    const isStep4Unlocked = isStep3Unlocked && !!(trackData?.subtitle && trackData?.highlights);
    const isStep5Unlocked = isStep4Unlocked && !!(trackData?.country && trackData?.city);
    // Museum indoor tracks do not collect tour length in Extra (only outdoor walk does), so
    // do not require trackData.length for iduniquemusee.
    const isStep6Unlocked =
        isStep5Unlocked && !!(trackData?.position && trackData?.duration && (isMuseum || trackData?.length));
    const isStep7Unlocked =
        isStep6Unlocked &&
        trackData?.unlockModeFree != null &&
        (trackData?.forceDownload === "none" ||
            trackData?.forceDownload === "recommend" ||
            trackData?.forceDownload === "force") &&
        (!isMuseum || (isMuseum && trackData?.museumSearchQrcode != null));

    // Build sidebar items
    const sidebarItems: SidebarItem[] = [
        {
            title: t("audioTourCategory"),
            link: `/create/tour/${linkId}/category`,
            prerequisitesMet: true,
        },
        {
            title: t("mainInformation"),
            subcategories: [
                {
                    name: t("title"),
                    link: `/create/tour/${linkId}/title`,
                    prerequisitesMet: isStep2Unlocked,
                },
                ...(isMuseum
                    ? [
                          {
                              name: t("museumInformation"),
                              link: `/create/tour/${linkId}/museum-info`,
                              prerequisitesMet: isStepMuseumInfoUnlocked,
                          },
                      ]
                    : []),
                {
                    name: t("descriptionAndHighlights"),
                    link: `/create/tour/${linkId}/description`,
                    prerequisitesMet: isStep3Unlocked,
                },
                {
                    name: t("location"),
                    link: `/create/tour/${linkId}/location`,
                    prerequisitesMet: isStep4Unlocked,
                },
                {
                    name: t("extraInformation"),
                    link: `/create/tour/${linkId}/extra`,
                    prerequisitesMet: isStep5Unlocked,
                },
                {
                    name: t("accessAndSecurity"),
                    link: `/create/tour/${linkId}/prices`,
                    prerequisitesMet: isStep6Unlocked,
                },
                {
                    name: t("photos"),
                    link: `/create/tour/${linkId}/photos`,
                    prerequisitesMet: isStep7Unlocked,
                },
            ],
            hasSubcategories: true,
        },
        ...(trackData && trackData.trackId
            ? [
                  // Image Scan Track: Content Section (step-by-step: mainTheme → objects → stories)
                  ...(isImageScan
                      ? [
                            {
                                title: t("content"),
                                subcategories: [
                                    {
                                        name: t("mainTheme"),
                                        link: `/create/tour/${linkId}/main-theme`,
                                        prerequisitesMet: true,
                                    },
                                    // Objects only shown after main theme is configured
                                    ...(hasScanConfig
                                        ? [
                                              {
                                                  name: t("objects"),
                                                  link: `/create/tour/${linkId}/objects`,
                                                  prerequisitesMet: true,
                                              },
                                          ]
                                        : []),
                                    // Stories only shown after objects exist
                                    ...(hasObjects
                                        ? [
                                              {
                                                  name: t("stories"),
                                                  link: `/create/tour/${linkId}/stories`,
                                                  prerequisitesMet: true,
                                              },
                                          ]
                                        : []),
                                ],
                                hasSubcategories: true,
                            },
                        ]
                      : []),
                  // Image Scan Track: Progression Section (if enabled and has stories)
                  // Show progression section when stories exist, but show steps incrementally
                  // Only levels are mandatory - badges and rewards are optional and shown after levels
                  ...(isImageScan && hasProgressionSystem && hasStories
                      ? [
                            {
                                title: t("progression"),
                                subcategories: [
                                    // Avatar - always shown once stories exist
                                    {
                                        name: t("avatar"),
                                        link: `/create/tour/${linkId}/avatar`,
                                        prerequisitesMet: true,
                                    },
                                    // Levels - shown after avatar is configured (mandatory)
                                    ...(hasAvatar
                                        ? [
                                              {
                                                  name: t("levels"),
                                                  link: `/create/tour/${linkId}/levels`,
                                                  prerequisitesMet: true,
                                              },
                                          ]
                                        : []),
                                    // Badges - shown after levels are configured (optional)
                                    ...(hasLevels
                                        ? [
                                              {
                                                  name: t("badges"),
                                                  link: `/create/tour/${linkId}/badges`,
                                                  prerequisitesMet: true,
                                              },
                                          ]
                                        : []),
                                    // Rewards - shown after levels are configured (optional, not dependent on badges)
                                    ...(hasLevels
                                        ? [
                                              {
                                                  name: t("rewards"),
                                                  link: `/create/tour/${linkId}/rewards`,
                                                  prerequisitesMet: true,
                                              },
                                          ]
                                        : []),
                                    // Social Share - shown after levels are configured (not dependent on badges/rewards)
                                    ...(hasLevels
                                        ? [
                                              {
                                                  name: t("socialMediaShare"),
                                                  link: `/create/tour/${linkId}/social-share`,
                                                  prerequisitesMet: true,
                                              },
                                          ]
                                        : []),
                                ],
                                hasSubcategories: true,
                            },
                        ]
                      : []),
                  ...(isMuseum ||
                  (!isMuseum && !isImageScan && trackData.waypoints?.length === 0 && !trackData.hasExcelGenerated)
                      ? [
                            {
                                title: isMuseum ? t("museumDesign") : t("audioTourGeneration"),
                                subcategories: !isMuseum
                                    ? [
                                          {
                                              name: t("itineraryAndTourCreation"),
                                              link: `/create/tour/${linkId}/itinerary`,
                                              prerequisitesMet: true,
                                          },
                                      ]
                                    : [
                                          {
                                              name: t("museumArchitecture"),
                                              link: `/create/tour/${linkId}/museum-architecture`,
                                              prerequisitesMet: true,
                                          },
                                      ],
                                hasSubcategories: true,
                            },
                        ]
                      : []),
                  // Publication section - only show for image scan tracks if progression is complete (or if no progression)
                  // For image scan with progression: show after levels are configured (badges/rewards are optional)
                  // For image scan without progression: show after stories exist
                  // For other tracks: show as before
                  ...((isImageScan && hasProgressionSystem && hasLevels) ||
                  (isImageScan && !hasProgressionSystem && hasStories) ||
                  isMuseum ||
                  !isImageScan
                      ? [
                            {
                                title: t("publication"),
                                subcategories: isMuseum
                                    ? [
                                          {
                                              name: t("stories"),
                                              link: `/create/tour/${linkId}/museum-stories`,
                                              prerequisitesMet: true,
                                          },
                                          {
                                              name: t("incompleteData"),
                                              link: `/create/tour/${linkId}/incomplete-data`,
                                              prerequisitesMet: true,
                                          },
                                          {
                                              name: t("publish"),
                                              link: `/create/tour/${linkId}/translate`,
                                              prerequisitesMet: true,
                                          },
                                          {
                                              name: t("qrCodesAndActivationLinks"),
                                              link: `/create/tour/${linkId}/qrcode`,
                                              prerequisitesMet: true,
                                          },
                                      ]
                                    : isImageScan
                                      ? [
                                            // Image scan tracks: simpler publication section
                                            {
                                                name: t("incompleteData"),
                                                link: `/create/tour/${linkId}/incomplete-data`,
                                                prerequisitesMet: true,
                                            },
                                            {
                                                name: t("publish"),
                                                link: `/create/tour/${linkId}/translate`,
                                                prerequisitesMet: true,
                                            },
                                            {
                                                name: t("qrCodesAndActivationLinks"),
                                                link: `/create/tour/${linkId}/qrcode`,
                                                prerequisitesMet: true,
                                            },
                                        ]
                                      : [
                                            // Regular tracks: full tour overview
                                            {
                                                name: t("mapAndContent"),
                                                link: `/create/tour/${linkId}/map-content`,
                                                prerequisitesMet: true,
                                            },
                                            ...(isHoho && trackData?.subType === "cl2j5yyhr0014k4qxtbgb4cpx"
                                                ? [
                                                      {
                                                          name: t("stops"),
                                                          link: `/create/tour/${linkId}/stop-content`,
                                                          prerequisitesMet: true,
                                                      },
                                                      {
                                                          name: t("busTracking"),
                                                          link: `/create/tour/${linkId}/bus-tracking`,
                                                          prerequisitesMet: true,
                                                      },
                                                  ]
                                                : []),
                                            ...(!isMuseum
                                                ? [
                                                      {
                                                          name: t("quiz"),
                                                          link: `/create/tour/${linkId}/quiz-content`,
                                                          prerequisitesMet: true,
                                                      },
                                                  ]
                                                : []),
                                            ...(isHoho
                                                ? [
                                                      {
                                                          name: t("playlistMusic"),
                                                          link: `/create/tour/${linkId}/bus-music`,
                                                          prerequisitesMet: true,
                                                      },
                                                  ]
                                                : []),
                                            ...(!isMuseum && !isImageScan
                                                ? [
                                                      {
                                                          name: t("contentPlaylist"),
                                                          link: `/create/tour/${linkId}/content-playlist`,
                                                          prerequisitesMet: true,
                                                      },
                                                  ]
                                                : []),
                                            {
                                                name: t("partners"),
                                                link: `/create/tour/${linkId}/partners`,
                                                prerequisitesMet: true,
                                            },
                                            {
                                                name: t("incompleteData"),
                                                link: `/create/tour/${linkId}/incomplete-data`,
                                                prerequisitesMet: true,
                                            },
                                            {
                                                name: t("publish"),
                                                link: `/create/tour/${linkId}/translate`,
                                                prerequisitesMet: true,
                                            },
                                            {
                                                name: t("qrCodesAndActivationLinks"),
                                                link: `/create/tour/${linkId}/qrcode`,
                                                prerequisitesMet: true,
                                            },
                                        ],
                                hasSubcategories: true,
                            },
                        ]
                      : []),
              ]
            : []),
    ];

    // Prevent navigation if prerequisites are not met
    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, prerequisitesMet: boolean): void => {
        if (!prerequisitesMet) {
            e.preventDefault();
        }
    };

    // Only show spinner on initial load, not on subsequent navigation
    if (isInitialLoading && !hasLoadedOnce.current) {
        return (
            <div className={`track-create-sidebar-navigation ${shell.navRoot}`}>
                <LoadingSpinner size={32} />
            </div>
        );
    }

    return (
        <div className={`track-create-sidebar-navigation ${shell.navRoot}`}>
            <ul className={`track-create-sidebar-nav ${shell.navList}`}>
                {sidebarItems.map((item, index) => (
                    <div key={index} className={shell.navGroup}>
                        {!item.hasSubcategories ? (
                            <li
                                className={`sidebar-title ${shell.navTitleLink} ${
                                    location.pathname === item.link ? shell.navTitleLinkActive : ""
                                } ${!item.prerequisitesMet ? shell.navTitleDisabled : ""}`}
                            >
                                <Link to={item.link || "#"} onClick={(e) => handleLinkClick(e, item.prerequisitesMet!)}>
                                    {item.title}
                                </Link>
                            </li>
                        ) : (
                            <>
                                <li className={`sidebar-title ${shell.navHeading}`}>{item.title}</li>
                                <ul className={`subcategory-list ${shell.navSubList}`}>
                                    {item.subcategories?.map((sub, subIndex) => (
                                        <li
                                            key={subIndex}
                                            className={`${shell.navSubItem} ${
                                                location.pathname === sub.link ? shell.navSubItemActive : ""
                                            } ${!sub.prerequisitesMet ? shell.navSubItemDisabled : ""}`}
                                        >
                                            <Link
                                                to={sub.link}
                                                onClick={(e) => handleLinkClick(e, sub.prerequisitesMet)}
                                            >
                                                {sub.name}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                ))}
            </ul>
        </div>
    );
};

export default Sidebar;
